// The platform's DesignerCatalog — what the shared column editors
// (@twasta/page-runtime/designerKit) read tables, columns, transactions,
// project variables and IOFlow APIs from inside the BUILDER, and how they show
// a message (react-hot-toast), a confirmation (the platform ConfirmDialog) and
// a dialog (the platform Modal).
//
// The editors moved into the shared package so the additional-attributes setup
// screen can use them on every host; the shims at the old paths
// (LookupEditor.tsx, ItemChangeRuleEditor.tsx, …) build this catalog and pass
// it on, so the designer behaves exactly as it did:
//   * tables / columns — the Database Designer's catalog (getTables /
//     getTableInfo), as ItemChangeRuleEditor / ValidationRuleEditor used;
//   * liveTables / liveColumns — the deployed preview schema, which the lookup
//     editor merges in (it always did);
//   * transactions — the lookup picker's "+ Add" objects.
// An empty project degrades every picker to free text (as before).

import { useMemo, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import type { DesignerCatalog, KitColumn, KitTable } from '@twasta/page-runtime/designerKit'
import Modal from '@/components/common/Modal'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { getTables, getTableInfo } from '@/api/designers'
import { listProjectVariables } from '@/api/projectVariables'
import {
  livePreviewTables, livePreviewColumns, livePreviewTransactions,
} from '@/livepreview/api'

/** The builder's catalog for one project, without a confirmation dialog
 *  (callers that need one use the hook below). Stable per project only when
 *  memoised by the caller. */
export function makePlatformDesignerCatalog(project: string): DesignerCatalog {
  const p = (project || '').trim()
  return {
    async tables(): Promise<KitTable[]> {
      if (!p) return []
      const res = await getTables(p)
      return Array.isArray(res?.tables) ? res.tables : []
    },
    async columns(table: string): Promise<KitColumn[]> {
      if (!p) return []
      const res = await getTableInfo(p, table)
      return Array.isArray(res?.table?.columns) ? res.table.columns : []
    },
    async liveTables(): Promise<KitTable[]> {
      if (!p) return []
      const res = await livePreviewTables(p)
      return (res?.tables || []).map((t) => ({ name: t.name }))
    },
    async liveColumns(table: string): Promise<KitColumn[]> {
      if (!p) return []
      const res = await livePreviewColumns(p, table)
      return res?.columns || []
    },
    async transactions() {
      if (!p) return []
      const res = await livePreviewTransactions(p)
      return res?.transactions || []
    },
    async projectVariables() {
      if (!p) return []
      const vars = await listProjectVariables(p)
      return (vars || []).map((v) => ({ name: v.name, label: v.description || v.name }))
    },
    async integrationApis() {
      if (!p) return []
      const { getIntegrationDesign } = await import('@/api/integration')
      const d = await getIntegrationDesign(p)
      return (d?.apis || []).filter((a) => a.active !== false).map((a) => a.api_name)
    },
    notify(kind, message) {
      if (kind === 'error') toast.error(message)
      else if (kind === 'success') toast.success(message)
      else toast(message)
    },
    Modal,
  }
}

/** `{catalog, confirmDialog}` for one project — the catalog memoised (the
 *  editors refetch when it changes) and wired to the platform ConfirmDialog,
 *  whose element the caller renders next to the editor. */
export function usePlatformDesignerCatalog(project: string): {
  catalog: DesignerCatalog
  confirmDialog: ReactNode
} {
  const { confirm, confirmDialog } = useConfirm()
  const catalog = useMemo<DesignerCatalog>(
    () => ({ ...makePlatformDesignerCatalog(project), confirm }),
    [project, confirm],
  )
  return { catalog, confirmDialog }
}
